import { AxiosInstance } from 'axios'
import { PrintingTemplate } from './printing-template'

export enum MedicalDocumentKind {
  PRESCRIPTION = 'Prescription',
  REPORT = 'Report',
  LME = 'LME',
  EXAM = 'ExamRequest',
}

export type MedicalDocumentType = typeof MedicalDocumentKind

export type MedicalDocumentReportData = {
  report: string
  references?: string
}
export type MedicalDocumentPrescriptionData = {
  prescriptions: {
    medicine: string
    posology: string
    custom?: boolean
  }
}

export type MedicalDocumentBody = {
  patientId: number
  kind: MedicalDocumentKind
  data: unknown
  templateId?: number
  withSignature?: boolean
  attendanceId?: number
}

export type MedicalDocument = {
  id: number
  kind: MedicalDocumentKind
  data: unknown
  withSignature: boolean
  printingTemplateId: number | null
  pharmaceuticalId: number
  physicianId: number
  patientId: number
  attendanceId: number | null

  printingTemplate: PrintingTemplate | null
}

export type DocumentSignatureAuth = {
  token: string
  expiresAt: Date | number
}

export type SignedDocument = {
  url: string
}

export function medicalDocumentEndpoints(apiClient: AxiosInstance) {
  return {
    async listByAttendance(attendanceId: number): Promise<MedicalDocument[]> {
      return apiClient.get(`get-medical-documents-by-attendance/${attendanceId}`)
    },
    async print(id: number, templateId: number | null = null): Promise<Blob> {
      return apiClient.get(`print-medical-document/${id}`, {
        responseType: 'blob',
        params: {
          templateId,
        },
      })
    },
    async authenticateDigitalSignature(cpf: string, otp: string): Promise<DocumentSignatureAuth> {
      return apiClient.post(`generate-birdid-token`, {
        cpf,
        otp,
      })
    },
    async getBirdIdToken(): Promise<DocumentSignatureAuth | ''> {
      return apiClient.get(`birdid-token/`)
    },
    async signDocument(documentId: string, token: string, fileData: Blob): Promise<SignedDocument> {
      const formData = new FormData()

      formData.append('documentId', documentId)
      formData.append('birdIdToken', token)
      formData.append('fileData', fileData)

      return apiClient.post(`sign-document/`, formData)
    },
    async create(body: MedicalDocumentBody): Promise<MedicalDocument> {
      return apiClient.post('create-medical-document', body)
    },
    async get(id: number): Promise<MedicalDocument> {
      return apiClient.get(`get-medical-document/${id}`)
    },
    async removeMedicalDocumentByKind(attendanceId: number, kind: MedicalDocumentKind): Promise<void> {
      return apiClient.delete(`remove-medical-document-by-kind/${attendanceId}/${kind}`)
    },
  }
}
